import React, { useState } from 'react';
import { BookingCartItem, useBookingCartStore } from '../stores/bookingCartStore';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { useI18n } from '../i18n/i18nContext';
import { formatInTimeZone } from 'date-fns-tz';
import { ru as ruLocale, et as etLocale, fi as fiLocale } from 'date-fns/locale';
import { ConfirmDialog } from './ConfirmDialog';
import { useServiceMetaStore } from '../stores/serviceMetaStore';

const TALLINN_TIMEZONE = 'Europe/Tallinn';

interface BookingCartProps {
  hideCheckoutButton?: boolean;
}

export const BookingCart: React.FC<BookingCartProps> = ({ hideCheckoutButton = false }) => {
  const { items, removeBooking, updateBooking } = useBookingCartStore();
  const { credits: serviceCredits, getMeta } = useServiceMetaStore();
  const navigate = useLocalizedNavigate();
  const { t, language } = useI18n();
  const dateLocale =
    language === 'ru'
      ? ruLocale
      : language === 'et'
        ? etLocale
        : language === 'fi'
          ? fiLocale
          : undefined;
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  const handleRemove = (cartItemId: string) => {
    setConfirmRemove(cartItemId);
  };

  const confirmRemoveBooking = () => {
    if (confirmRemove) {
      removeBooking(confirmRemove);
      setConfirmRemove(null);
    }
  };

  const handleChange = (item: BookingCartItem) => {
    const startTime = new Date(item.startTime);
    const preselectedTime = formatInTimeZone(startTime, TALLINN_TIMEZONE, 'HH:mm');
    let navigationState = {
      preselectedDate: startTime,
      preselectedTime: preselectedTime,
      isChanging: true,
      cartItemId: item.cartItemId,
    };
    if (item.type === 'service') {
      navigate(`/book/${item.id}`, {
        state: navigationState,
      });
    } else {
      let navigationState = {
        isChanging: true,
        cartItemId: item.cartItemId,
        preselectedServices: item.services.map((service) => ({
          serviceId: service.serviceId,
          startTime: service.startTime,
        })),
      };
      navigate(`/book/package/${item.id}`, {
        state: navigationState,
      });
    }
  };

  const getTitle = () => {
    const count = items.length;
    return count === 1
      ? t.cart.bookingSelected?.replace('{{count}}', count.toString()) ||
          `${count} Booking Selected`
      : t.cart.bookingsSelected?.replace('{{count}}', count.toString()) ||
          `${count} Bookings Selected`;
  };

  if (items.length === 0) {
    return null;
  }

  const sortedItems = [...items].sort((a, b) => {
    return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
  });

  return (
    <>
      {confirmRemove && (
        <ConfirmDialog
          title={t.cart.removeBookingTitle || 'Remove Booking'}
          message={t.cart.confirmRemove || 'Are you sure you want to remove this booking?'}
          confirmText={t.cart.remove || 'Remove'}
          cancelText={t.common.cancel || 'Cancel'}
          onConfirm={confirmRemoveBooking}
          onCancel={() => setConfirmRemove(null)}
          type="danger"
        />
      )}
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-md">
        <h2 className="mb-4 text-xl font-bold text-gray-900">{getTitle()}</h2>

        <div className="mb-4 space-y-4">
          {sortedItems.map((item) => {
            const meta = item.type === 'service' ? getMeta(item.id) : null;
            // Default to 1 to prevent errors if meta is not found or is zero.
            const maxParticipants = meta?.maxParticipants || 1;
            const isServiceItem = item.type === 'service';

            return (
              <div key={item.cartItemId} className="border-b border-gray-200 pb-4">
                <div
                  onClick={() => handleChange(item)}
                  className={`flex cursor-pointer items-start justify-between rounded-md p-3 transition-colors hover:bg-gray-50`}
                >
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">
                      {item.name}
                      {serviceCredits[item.id] > 1 && (
                        <span className="ml-2 text-sm font-normal text-gray-500">
                          ({serviceCredits[item.id]} {t.cart.credits})
                        </span>
                      )}
                    </p>
                    {item.type === 'package' ? (
                      <div className="mt-2 space-y-2">
                        {item.services.map((service) => (
                          <div key={service.serviceId} className="p-2">
                            <p className="text-sm font-medium text-gray-700">
                              {service.serviceName}
                            </p>
                            <p className="text-sm text-gray-600">
                              {formatInTimeZone(
                                new Date(service.startTime),
                                TALLINN_TIMEZONE,
                                'EEE, MMM d',
                                {
                                  locale: dateLocale,
                                }
                              )}{' '}
                              {t.cart.at}{' '}
                              {formatInTimeZone(
                                new Date(service.startTime),
                                TALLINN_TIMEZONE,
                                'HH:mm'
                              )}
                            </p>
                            <p className="text-sm text-gray-500">
                              {service.duration} {t.booking.minutes}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-gray-600">
                          {formatInTimeZone(
                            new Date(item.startTime),
                            TALLINN_TIMEZONE,
                            'EEE, MMM d',
                            {
                              locale: dateLocale,
                            }
                          )}{' '}
                          {t.cart.at}{' '}
                          {formatInTimeZone(new Date(item.startTime), TALLINN_TIMEZONE, 'HH:mm')}
                        </p>
                        <p className="text-sm text-gray-500">
                          {item.duration} {t.booking.minutes}
                        </p>
                      </>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleChange(item);
                      }}
                      className="text-sm font-medium text-blue-600 hover:text-blue-800"
                    >
                      {t.cart.change || 'Change'}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemove(item.cartItemId);
                      }}
                      className="text-sm font-medium text-red-500 hover:text-red-700"
                    >
                      {t.cart.remove}
                    </button>
                  </div>
                </div>

                {/* Render quantity controls only if the service supports more than one participant */}
                {isServiceItem && maxParticipants > 1 && (
                  <div className="mt-2 flex items-center space-x-3">
                    <span className="text-sm font-medium text-gray-700">{t.cart.participants}</span>
                    <button
                      type="button"
                      onClick={() => {
                        const newQuantity = Math.max(1, item.quantity - 1);
                        // CRITICAL: When decreasing quantity, we must also shorten the invitees
                        // array to prevent holding onto emails for participants who no longer exist.
                        const newInvitees = item.invitees.slice(0, newQuantity - 1);
                        updateBooking(item.cartItemId, {
                          quantity: newQuantity,
                          invitees: newInvitees,
                        });
                      }}
                      disabled={item.quantity <= 1}
                      className="rounded-md border px-2 py-1 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      -
                    </button>
                    <span className="text-lg font-bold">{item.quantity}</span>
                    <button
                      type="button"
                      onClick={() => {
                        const newQuantity = Math.min(maxParticipants, item.quantity + 1);
                        updateBooking(item.cartItemId, { quantity: newQuantity });
                      }}
                      disabled={item.quantity >= maxParticipants}
                      className="rounded-md border px-2 py-1 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      +
                    </button>
                  </div>
                )}

                {/* Render email inputs if quantity is greater than 1 */}
                {isServiceItem && item.quantity > 1 && (
                  <div className="mt-3 space-y-2 border-l-2 border-gray-200 pl-2">
                    <p className="text-sm font-semibold text-gray-600">
                      {t.cart.participantEmails}
                    </p>
                    {/* Create a temporary array of the correct length to map over and render inputs */}
                    {Array.from({ length: item.quantity - 1 }).map((_, index) => (
                      <input
                        key={index}
                        type="email"
                        placeholder={t.cart.participantEmailPlaceholder.replace(
                          '{{count}}',
                          (index + 2).toString()
                        )}
                        className="w-full rounded-md border p-2 transition focus:border-blue-500 focus:ring-blue-500"
                        value={item.invitees[index] || ''}
                        onChange={(e) => {
                          // IMPORTANT: Create a new array to avoid direct state mutation.
                          const newInvitees = [...item.invitees];
                          newInvitees[index] = e.target.value;
                          updateBooking(item.cartItemId, { invitees: newInvitees });
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {!hideCheckoutButton && (
          <div className="flex justify-end">
            <button
              onClick={() => navigate('/checkout')}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-green-600 px-6 py-2 font-medium text-white hover:bg-green-700 sm:w-auto"
            >
              <span>✓</span>
              {t.cart.proceedToCheckout}
            </button>
          </div>
        )}
      </div>
    </>
  );
};
